import React from 'react'

import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_domain_verification'

const URLS = {
  CPL_SETUP: 'https://campus.barracuda.com/doc/3866715/'
}

function DomainVerificationI18n({
  classes,
  productName,
  hasCpl
}: {
  classes: ReturnType<typeof styles>
  productName?: string
  hasCpl?: boolean
}) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)

  return (
    <div className={classes.content} data-testid="content">
      <p>
        {formatMessage('intro', { productName })}
        <br />
        <br />
      </p>
      {hasCpl && (
        <>
          {formatMessage('cpl.description_prefix')}
          <a href={URLS.CPL_SETUP} target="_new">
            {formatMessage('cpl.description_link')}
          </a>{' '}
          {formatMessage('cpl.description_suffix')}
        </>
      )}
      {!hasCpl && (
        <>
          {formatMessage('non_cpl.intro')}
          <ul>
            <li>
              <b>{formatMessage('non_cpl.methods.mx_records.title')}</b>
              <br />
              {formatMessage('non_cpl.methods.mx_records.description', { productName })}
              <p>{formatMessage('non_cpl.methods.mx_records.view_config')}</p>
            </li>
            <li>
              <b>{formatMessage('non_cpl.methods.cname_records.title')}</b>
              <br />
              {formatMessage('non_cpl.methods.cname_records.note')}
            </li>
            <ol>
              <li>{formatMessage('non_cpl.methods.cname_records.step_1')}</li>
              <li>
                {formatMessage('non_cpl.methods.cname_records.step_2')}
                <br />
                {formatMessage('non_cpl.methods.cname_records.step_2_note')}
              </li>
              <li>
                {formatMessage('non_cpl.methods.cname_records.step_3')}
                <br />
                {formatMessage('non_cpl.methods.cname_records.step_3_note')}
              </li>
            </ol>
            <li>
              <b>{formatMessage('non_cpl.methods.email_technical_contact.title')}</b>
              <br />
              {formatMessage('non_cpl.methods.email_technical_contact.note', { productName })}
              <br />
              {formatMessage('non_cpl.methods.email_technical_contact.description')}
            </li>
            <li>
              <b>{formatMessage('non_cpl.methods.email_postmaster.title')}</b>
              <br />
              {formatMessage('non_cpl.methods.email_postmaster.note', { productName })}
              <br />
              {formatMessage('non_cpl.methods.email_postmaster.description')}
            </li>
          </ul>
        </>
      )}
    </div>
  )
}

function DomainVerification(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <DomainVerificationI18n classes={classes} productName={props.productName} hasCpl={props.hasCpl} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <p>
        Each of the domains where you want to filter email must be verified by {props.productName} for proof of
        ownership; {props.productName} does not process email for a domain until the verification process is complete.
        <br />
        <br />
      </p>
      {props.hasCpl && (
        <>
          To verify ownership, you must first connect your Barracuda Email Security Gateway to Barracuda Cloud Control
          from the <b>ADVANCED &gt; Cloud Control</b> page in the Barracuda Email Security Gateway web interface. Point
          the MX record for the domain to this same Barracuda Email Security Gateway by modifying your DNS server. For
          more information, see{' '}
          <a href="https://campus.barracuda.com/doc/3866715/" target="_new">
            How to Set Up Your Cloud Protection Layer
          </a>{' '}
          in Barracuda Campus.
        </>
      )}
      {!props.hasCpl && (
        <>
          Click the <b>Verify Ownership</b> link in the <b>Domains Name</b> column to go to the{' '}
          <b>Domains &gt; Domain Settings</b> page to complete the verification process using one of the following
          methods:
          <ul>
            <li>
              <b>MX Records</b>
              <br />
              Add one or both of the Barracuda Networks Mail Exchange (MX) records to your domain&apos;s DNS server.
              Assign these records a higher priority than your domain&apos;s existing MX records to ensure your mail
              continues to flow until your {props.productName} domain is validated. Note that it can take up to 24 hours
              for the {props.productName} DNS server to recognize this change in your MX records. Once your domain is
              validated, you can remove the old MX records, leaving just the Barracuda Networks MX records. Your mail is
              now sent to {props.productName} for scanning.
              <p>
                To view the MX record configuration or mail statistics for a verified domain, go to the <b>Domains</b>{' '}
                page, and click the domain name in the <b>Domain Name</b> column to toggle the MX record configuration
                details.
              </p>
            </li>
            <li>
              <b>CNAME Records</b>
              <br />
              Note: You must have access to your DNS server to use this verification method.
            </li>
            <ol>
              <li>
                Log into your DNS Server and, under this domain, create a subdomain whose name is created by
                concatenating &apos;barracuda&apos; and the CNAME token. For example:{' '}
                <code>barracuda30929916985.mydomain.com</code>
              </li>
              <li>
                Point the CNAME record of that subdomain to: <code>ess.barracuda.com</code>
                <br />
                Note: Allow the DNS propagation to take effect before proceeding.
              </li>
              <li>
                Click <b>Next</b> to complete the domain verification process.
                <br />
                Note: To view the validation status, click <b>Edit</b> in the <b>Settings</b> column on the{' '}
                <b>Domains</b> page for the selected domain. In the <b>Domain &gt; Domain Settings</b> page, scroll to
                the
                <b>Encryption</b> section to view the <b>Validation Status</b>.
              </li>
            </ol>
            <li>
              <b>Email to the domain&apos;s technical contact</b>
              <br />
              <b>Note:</b> This verification option is available only when {props.productName} can find your technical
              contact in your domain&apos;s WHOIS entry.
              <br />
              This method sends a verification email to the technical contact email address listed on your domain&apos;s
              WHOIS entry. The confirmation email includes a link that the recipient must click to verify the domain.
            </li>
            <li>
              <b>Email to the Postmaster</b>
              <br />
              <b>Note:</b> This verification option is available only when {props.productName} can find your postmaster
              in your domain&apos;s WHOIS records.
              <br />
              This method sends a verification email to the postmaster email address for your domain. The confirmation
              email includes a link that the recipient must click to verify the domain.
            </li>
          </ul>
        </>
      )}
    </div>
  )
}

export default DomainVerification
